import { Provider as GadgetProvider } from "@gadgetinc/react";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
  useRouteError,
} from "react-router";
import { Suspense, useEffect } from "react";
import { api } from "./api";
import "./app.css";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider, themeInlineScript } from "@/lib/theme";
import { HydrationDebugBanner } from "@/components/dev/HydrationDebugBanner";
import { GeoProvider, getVisitorGeo, type VisitorGeo } from "@barrykilby/geo";
import {
  LocaleProvider,
  localeForCountry,
  type Locale,
} from "@/lib/strings";
import {
  FlashToastHost,
  registerSidebarToastAdapter,
} from "@barrykilby/ui/patterns";
import { toast as sonnerToast } from "sonner";
import { initPostHog } from "@/lib/analytics";
import { useReportRouteError } from "@/lib/useReportRouteError";
import type { GadgetConfig } from "gadget-server";
import type { Route } from "./+types/root";

const isProduction = process.env.NODE_ENV === "production";

export const links = () => [
  { rel: "icon", type: "image/svg+xml", href: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Cpath d='M92 5L92 35C92 43 86 49 78 49L50 49C44 49 40 45 40 39L40 25C40 15 48 5 58 5Z' fill='%23f59e0b'/%3E%3Cpath d='M8 95L8 65C8 57 14 51 22 51L50 51C56 51 60 55 60 61L60 75C60 85 52 95 42 95Z' fill='%23f59e0b'/%3E%3C/svg%3E" },
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
  { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@600;700;800&family=Outfit:wght@400;500&display=swap" },
];

export const meta = () => [
  // charset is rendered statically in <head> at the top of the
  // document so it's guaranteed to land in the first 1024 bytes;
  // see Layout export below.
  // viewport + format-detection are rendered statically in <head>
  // below (alongside charSet) so child routes that define their own
  // meta() can't accidentally clobber them. RR v7 child meta REPLACES
  // parent meta unless the child explicitly merges via `matches`,
  // which our /for/<trade>, /s/<token>, /p/<token>
  // routes don't -- and missing format-detection on iOS Safari
  // causes hydration mismatches on every marketing page.
  { title: "Manos -- Spend the day on your customers, not your computer" },
  // Currency-free description -- the actual promo price varies by
  // country (resolved on each marketing page via getVisitorGeo +
  // promoMonthlyDisplay). The meta description is a single string
  // baked at build time, so we keep it currency-agnostic.
  { name: "description", content: "Jobs, clients, and invoices for sole traders, pairs, and small teams running every kind of service business. 3 days free, then a low monthly price." },
  // Brand colour for browser chrome (mobile address bar, PWA splash).
  { name: "theme-color", content: "#FA9805" },
  { property: "og:site_name", content: "Manos" },
  { property: "og:title", content: "Manos -- Spend the day on your customers, not your computer" },
  { property: "og:description", content: "Spend the day on your customers, not your computer. Manos handles the admin so you can focus on the work." },
  { property: "og:type", content: "website" },
  { property: "og:url", content: "https://manos.team" },
  // OG image: 1200x630 white-on-amber lockup + tagline. Lives at
  // /og-image.png in the public dir; cached by Facebook/LinkedIn/etc.
  // for ~24h after first scrape, so changes can lag in shared posts.
  { property: "og:image", content: "https://manos.team/og-image.png" },
  { property: "og:image:width", content: "1200" },
  { property: "og:image:height", content: "630" },
  { property: "og:image:alt", content: "Manos -- spend the day on your customers, not your computer" },
  // summary_large_image renders the OG image as a full-width card
  // on Twitter/X instead of the small thumbnail "summary" shape.
  { name: "twitter:card", content: "summary_large_image" },
  { name: "twitter:title", content: "Manos -- Spend the day on your customers, not your computer" },
  { name: "twitter:description", content: "Spend the day on your customers, not your computer." },
  { name: "twitter:image", content: "https://manos.team/og-image.png" },
];

export type RootOutletContext = {
  gadgetConfig: GadgetConfig;
  csrfToken: string;
  visitorGeo: VisitorGeo;
  locale: Locale;
};

/**
 * Markets with first-class regionalisation. Passing this allowlist
 * to getVisitorGeo enables the upstream `?as=<country>` URL override
 * for QA. Every loader that resolves visitor geo passes the same
 * list so the override behaves consistently across surfaces.
 */
export const ALLOW_AS_COUNTRIES = ["GB", "US", "CA", "IE", "AU", "NZ"] as const;

export const loader = async ({ context, request }: Route.LoaderArgs) => {
  const { session, gadgetConfig } = context;

  const visitorGeo = getVisitorGeo(request, {
    allowAsOverride: ALLOW_AS_COUNTRIES,
  });

  // Public-side locale: visitor's CF country (or ?as= override).
  // Trader-facing routes wrap their own LocaleProvider in _app.tsx
  // using profile.country.
  const locale: Locale = localeForCountry(visitorGeo.country);

  return {
    gadgetConfig,
    csrfToken: session?.get("csrfToken"),
    visitorGeo,
    locale,
    // Surface the PostHog public API key to the client so analytics.ts
    // can lazy-init. Public-by-design (PostHog "phc_*" keys are write-
    // only). When unset on the env, the analytics module silently
    // no-ops everywhere -- safe to ship before the project exists.
    posthogApiKey: process.env.POSTHOG_API_KEY ?? null,
    // Gadget's actual environment name ("development" | "production").
    // Surfaced to the client because the frontend bundle is built with
    // NODE_ENV=production for BOTH Gadget environments, so reading
    // NODE_ENV in analytics.ts would have every dev event self-report
    // as "production" and pollute the PostHog dashboard.
    gadgetEnvironment:
      process.env.GADGET_ENV ?? process.env.GADGET_PUBLIC_APP_ENV ?? "unknown",
  };
};

/**
 * Document shell. RR v7 framework mode auto-wraps both the route
 * component (`App`) and the route's `ErrorBoundary` in this Layout,
 * so the `<html><head><body>` lives in exactly one place. Matches
 * Gadget's documented React Router pattern; replaces the previous
 * structure where App rendered its own shell AND the imported
 * `ProductionErrorBoundary` from gadget-server/react-router rendered
 * a second one (which carries an internal double-`<head>` JSX bug
 * that masked real production errors with a noisy SSR failure).
 */
export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        {/* Charset MUST be in the first 1024 bytes of the document
            (HTML spec) so the parser commits to UTF-8 before reading
            any non-ASCII bytes in head/body. Render it as a static
            element BEFORE <Meta /> so byte order is guaranteed,
            regardless of how RR v7 orders the meta() array. The
            httpEquiv pair is belt-and-suspenders for older agents
            and proxies that strip Content-Type charset. */}
        <meta charSet="utf-8" />
        <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
        {/* Capture the SSR <body> HTML BEFORE React hydrates so the
            HydrationDebugBanner can diff it against the iOS-Safari-
            mutated DOM. Inline because by the time React mounts, any
            iOS data-detector / content-blocker mutations have already
            happened. Behind a query flag so it's free for normal
            traffic. Strip after debugging. */}
        <script
          dangerouslySetInnerHTML={{
            __html:
              "try{if(new URLSearchParams(location.search).get('debug')==='1'){requestAnimationFrame(function(){if(document.body)window.__manosSSRBody=document.body.outerHTML});}}catch(e){}",
          }}
        />
        {/* viewport + format-detection are document-level concerns
            that must survive route meta() overrides. RR v7's per-
            route meta REPLACES the parent unless the child merges
            via `matches`, and several routes (/for/<trade>, /s/<t>,
            /p/<t>) don't merge. Rendering these statically
            guarantees they always reach the browser. */}
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
        <meta name="format-detection" content="telephone=no, date=no, address=no" />
        {/* Favicon set + apple-touch-icon. Rendered statically here
            (not via meta()) because Links rel-list survives route
            replacements differently from <meta>; pinning to the
            document head guarantees iOS sharing previews and
            browser tabs always show the Manos amber tile. */}
        <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
        <Meta />
        <Links />
        {/* Apply theme before paint — reads localStorage then falls back to system pref */}
        <script dangerouslySetInnerHTML={{ __html: themeInlineScript }} />
        {!isProduction && <script type="module" src="/@vite/client" async />}
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App({ loaderData }: Route.ComponentProps) {
  const { gadgetConfig, csrfToken, visitorGeo, locale } = loaderData;

  // Register the sidebar (sonner) adapter once so `notify.ambientSidebar`
  // has a destination. Safe to re-run -- `registerSidebarToastAdapter`
  // just overwrites. Kept inside the component so HMR re-runs it.
  useEffect(() => {
    registerSidebarToastAdapter(({ title, description }) => {
      sonnerToast.info(title, { description });
    });
    return () => registerSidebarToastAdapter(null);
  }, []);

  // PostHog lazy-init. Only fires once per page load on the client;
  // SSR is a no-op (no window). Graceful no-op when the api key is
  // null (env var unset) so this can ship pre-PostHog-project.
  useEffect(() => {
    void initPostHog(loaderData.posthogApiKey, {
      environment: loaderData.gadgetEnvironment,
      app: "manos",
    });
  }, [loaderData.posthogApiKey, loaderData.gadgetEnvironment]);

  // Hydration marker. The Playwright fixture (tests/e2e/fixtures/test.ts)
  // waits on `window.__manosHydrated` after each `page.goto` so the
  // next click() doesn't race React's event-listener attachment. On
  // SSR the effect is a no-op (no window). On the client it fires
  // exactly once after the first commit, at which point React's
  // onClick listeners are attached and a Playwright click() is
  // guaranteed to reach a hydrated handler.
  useEffect(() => {
    if (typeof window === "undefined") return;
    (window as any).__manosHydrated = true;
  }, []);

  return (
    <Suspense>
      <GadgetProvider api={api}>
        <ThemeProvider>
          <GeoProvider visitor={visitorGeo}>
            <LocaleProvider locale={locale}>
              <Outlet context={{ gadgetConfig, csrfToken, visitorGeo, locale }} />
              <Toaster richColors closeButton />
              <FlashToastHost />
              <HydrationDebugBanner />
            </LocaleProvider>
          </GeoProvider>
        </ThemeProvider>
      </GadgetProvider>
    </Suspense>
  );
}

/**
 * Custom root error boundary. Replaces the imported
 * `ProductionErrorBoundary` / `DevelopmentErrorBoundary` from
 * gadget-server/react-router because their `ProductionErrorPage`
 * has a double-`<head>` JSX bug that throws "head may only be
 * rendered once" during SSR, masking the underlying error.
 *
 * Renders inner content only -- Layout above provides the document
 * shell. In dev, surfaces the stack trace inline so iteration is
 * fast; in prod, just the message and a return-home affordance.
 */
export function ErrorBoundary() {
  const error = useRouteError();
  const isDev = !isProduction;
  // Fire the admin notification + PostHog $exception capture for any
  // error that escapes past every nested ErrorBoundary (i.e. a crash
  // at root or in a route that has no boundary of its own). Without
  // this, errors in /admin/*, /sign-in, the marketing pages, etc.
  // would only land in the dev console and never page Barry.
  // referenceId is read below for the user-facing copy.
  const { referenceId } = useReportRouteError("root");

  let title: string;
  let message: string | undefined;
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    title = `${error.status} ${error.statusText || ""}`.trim();
    message =
      typeof error.data === "string"
        ? error.data
        : error.data
          ? JSON.stringify(error.data)
          : undefined;
  } else if (error instanceof Error) {
    title = "Application Error";
    message = error.message;
    stack = error.stack;
  } else {
    title = "Unexpected error";
    message = typeof error === "string" ? error : undefined;
  }

  return (
    <div
      style={{
        fontFamily: "system-ui, -apple-system, sans-serif",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1.5rem",
        backgroundColor: "#fafaf9",
        color: "#1a1a1a",
      }}
    >
      <div style={{ maxWidth: "640px", width: "100%", textAlign: "center" }}>
        <h1 style={{ fontSize: "2rem", fontWeight: 700, marginBottom: "0.75rem" }}>
          {title}
        </h1>
        {message && (
          <p style={{ color: "#52525b", marginBottom: "1rem" }}>{message}</p>
        )}
        {referenceId && (
          <p
            style={{
              color: "#71717a",
              marginBottom: "1.5rem",
              fontSize: "0.75rem",
              fontFamily: "ui-monospace, SFMono-Regular, monospace",
            }}
          >
            Reference: {referenceId}
          </p>
        )}
        <a
          href="/inbox"
          style={{
            display: "inline-block",
            padding: "0.625rem 1.25rem",
            backgroundColor: "#d97706",
            color: "white",
            borderRadius: "0.375rem",
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          Return home
        </a>
        {isDev && stack && (
          <pre
            style={{
              marginTop: "1.5rem",
              padding: "1rem",
              backgroundColor: "#27272a",
              color: "#fafafa",
              borderRadius: "0.375rem",
              fontSize: "0.75rem",
              textAlign: "left",
              overflow: "auto",
              whiteSpace: "pre-wrap",
            }}
          >
            {stack}
          </pre>
        )}
      </div>
    </div>
  );
}
